import axios from "@/utils/axios";
import type { FormattedForm } from "@/workflow/components/configurations/form/FormDataFormatter";
import type {
  ApiNode,
  Edge,
  Input,
  RefName,
} from "@/workflow_api/types/workflow";
import { useToast } from "@elaniintc/goes-design-system";
import type {
  ExternalData,
  ExternalDataFunction,
  ExternalDataType,
} from "@sdt/render-form-engine";
import {
  QueryClient,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";

interface ValidationError {
  response?: {
    status?: number;
    data?: {
      errors?: Record<string, string[]>;
      message?: string;
    };
  };
}

interface NodeBody {
  ref_name: RefName;
  display_name: string;
  position_x: number;
  position_y: number;
  flow_id: string;
  type:
    | "GENERAL"
    | "IF_CONDITION"
    | "SWITCH_CONDITION"
    | "WEBHOOK"
    | "FORM"
    | "REVISION"
    | "DOCUMENT_GENERATOR"
    | "DIGITAL_SIGNATURE"
    | "PAYMENT"
    | "DATA_CENTER"
    | "EXPEDIENTE"
    | "MULTITHREAD"
    | "WAIT"
    | "NOTIFICATION"
    | "INTEROPERABILITY"
    | "SIGN_DOCUMENT"
    | "N8N"
    | "MERGE";
  edge?: Edge;
}

export const useCreateNode = (id: string) => {
  const query = useQueryClient();
  const toast = useToast();
  return useMutation({
    mutationFn: async (data: { node: NodeBody }) => {
      const response = await axios.post("/admin/v1/node", { ...data.node });
      return response.data;
    },
    onSuccess() {
      query.invalidateQueries({
        queryKey: ["admin-flow-detail", id],
      });
      toast.showToast({
        title: "Nodo creado",
        description: "El nodo ha sido creado correctamente.",
        options: {
          type: "success",
          duration: 5000,
        },
      });
    },
    onError(error: ValidationError) {
      let title = "Error al crear nodo";
      let description = "Ocurrió un error al intentar crear el nodo.";

      // Verificar si es un error de validación específico
      if (error?.response?.status === 422) {
        const validationErrors = error?.response?.data?.errors;

        if (validationErrors?.display_name?.includes("validation.unique")) {
          title = "Nombre duplicado";
          description =
            "Ya existe un nodo con este nombre. Por favor, elige un nombre diferente.";
        } else if (validationErrors?.ref_name?.includes("validation.unique")) {
          title = "Referencia duplicada";
          description =
            "Ya existe un nodo con esta referencia. Por favor, cambia la referencia.";
        } else {
          // Otros errores de validación 422
          title = "Error de validación";
          description =
            "Los datos proporcionados no son válidos. Verifica la información e intenta nuevamente.";
        }
      } else if (error?.response?.status === 403) {
        title = "Sin permisos";
        description = "No tienes permisos para crear nodos en este flujo.";
      }

      toast.showToast({
        title,
        description,
        options: {
          type: "error",
          duration: 5000,
        },
      });
    },
  });
};

export interface UpdateNodeBody {
  flow_id: string;
  ref_name: RefName;
  display_name: string;
  type: string;
  order: number;
  position_x: number;
  position_y: number;
  manual_continue?: boolean;
  edge?: Edge | null;
  edges?: Array<{
    source: string;
    source_handler: string;
    target_handler: string;
    target_input_name: string;
  }>;
  new_node?: NodeBody;
  inputs?: Input;
  connections?: Record<
    string,
    { display_name: string; target_node: string | null }
  >;
}
export interface UpdateNodePayload {
  node_id: string;
  node: UpdateNodeBody;
}

export const useUpdateNode = (flowId: string) => {
  const query = useQueryClient();
  const toast = useToast();
  return useMutation({
    mutationFn: async (data: UpdateNodePayload) => {
      const response = await axios.patch<{ data: ApiNode }>(
        `/admin/v1/node/${data.node_id}`,
        {
          ...data.node,
        },
      );
      return response.data;
    },
    onSuccess() {
      query.invalidateQueries({
        queryKey: ["admin-flow-detail", flowId],
      });
      toast.showToast({
        title: "Nodo actualizado",
        description: "El nodo ha sido actualizado correctamente.",
        options: {
          type: "success",
          duration: 5000,
        },
      });
    },
    onError(error: ValidationError) {
      let title = "Error al actualizar nodo";
      let description = "Ocurrió un error al intentar actualizar el nodo.";

      // Verificar si es un error de validación específico
      if (error?.response?.status === 422) {
        const validationErrors = error?.response?.data?.errors;

        if (validationErrors?.display_name?.includes("validation.unique")) {
          title = "Nombre duplicado";
          description =
            "Ya existe un nodo con este nombre. Por favor, elige un nombre diferente.";
        } else if (validationErrors?.ref_name?.includes("validation.unique")) {
          title = "Referencia duplicada";
          description =
            "Ya existe un nodo con esta referencia. Por favor, cambia la referencia.";
        } else {
          // Otros errores de validación 422
          title = "Error de validación";
          description =
            "Los datos proporcionados no son válidos. Verifica la información e intenta nuevamente.";
        }
      } else if (error?.response?.status === 404) {
        title = "Nodo no encontrado";
        description = "El nodo que intentas actualizar no existe.";
      } else if (error?.response?.status === 403) {
        title = "Sin permisos";
        description = "No tienes permisos para actualizar este nodo.";
      }

      toast.showToast({
        title,
        description,
        options: {
          type: "error",
          duration: 5000,
        },
      });
    },
  });
};

export const useResetNode = () => {
  return useMutation({
    mutationFn: async (data: UpdateNodePayload) => {
      const response = await axios.patch(`/admin/v1/node/${data.node_id}`, {
        ...data.node,
        inputs: [],
      });
      return response.data;
    },
  });
};

export const useDeleteNode = (id: string) => {
  const query = useQueryClient();
  const toast = useToast();
  return useMutation({
    mutationFn: async (data: { node_id: string }) => {
      const response = await axios.delete(`/admin/v1/node/${data.node_id}`);
      return response.data;
    },
    onSuccess() {
      query.invalidateQueries({
        queryKey: ["admin-flow-detail", id],
      });
      toast.showToast({
        title: "Nodo eliminado",
        description: "El nodo ha sido eliminado correctamente.",
        options: {
          type: "success",
          duration: 5000,
        },
      });
    },
    onError() {
      toast.showToast({
        title: "Error al eliminar el nodo",
        description:
          "Ocurrió un error al intentar eliminar el nodo, intente nuevamente.",
        options: {
          type: "error",
          duration: 5000,
        },
      });
    },
  });
};

export const useMasiveDelete = (id: string) => {
  const query = useQueryClient();
  const toast = useToast();
  return useMutation({
    mutationFn: async (data: { nodes: string[] }) => {
      const response = await axios.post(`/admin/v1/node/multiple`, {
        nodes: data.nodes,
      });
      return response.data;
    },
    onSuccess() {
      query.invalidateQueries({
        queryKey: ["admin-flow-detail", id],
      });
      toast.showToast({
        title: "Nodos eliminados",
        description: "Los nodos han sido eliminados correctamente.",
        options: {
          type: "success",
          duration: 5000,
        },
      });
    },
    onError() {
      toast.showToast({
        title: "Error al eliminar nodos",
        description: "Ocurrió un error al intentar eliminar los nodos.",
        options: {
          type: "error",
          duration: 5000,
        },
      });
    },
  });
};

export const useUpdateNodesPositions = () => {
  const toast = useToast();
  return useMutation({
    mutationFn: async (data: {
      nodes: { id: string; position_x: number; position_y: number }[];
    }) => {
      const response = await axios.post(`/admin/v1/node/position`, {
        nodes: data.nodes,
      });
      return response.data;
    },
    onSuccess() {
      toast.showToast({
        title: "Posiciones de nodos actualizadas",
        description:
          "Las posiciones de los nodos han sido actualizadas correctamente.",
        options: {
          type: "success",
          duration: 5000,
        },
      });
    },
    onError() {
      toast.showToast({
        title: "Error al actualizar posiciones",
        description:
          "Ocurrió un error al intentar actualizar las posiciones de los nodos.",
        options: {
          type: "error",
          duration: 5000,
        },
      });
    },
  });
};

// save form
export const useCreateForm = () => {
  const query = useQueryClient();
  const toast = useToast();

  return useMutation({
    mutationFn: async (data: { form: FormattedForm }) => {
      const response = await axios.post("/admin/v1/forms", { ...data.form });
      return response.data;
    },
    onSuccess() {
      query.invalidateQueries({ queryKey: ["admin-forms"] });
      toast.showToast({
        title: "Formulario creado",
        description: "El formulario ha sido creado correctamente.",
        options: {
          type: "success",
          duration: 5000,
        },
      });
    },
    onError(error: ValidationError) {
      let title = "Error al crear formulario";
      let description = "Ocurrió un error al intentar crear el formulario.";

      if (error?.response?.status === 422) {
        const validationErrors = error?.response?.data?.errors;

        if (validationErrors?.name?.includes("validation.unique")) {
          title = "Nombre duplicado";
          description =
            "Ya existe un formulario con este nombre. Por favor, elige un nombre diferente.";
        } else {
          title = "Error de validación";
          description =
            "Los datos del formulario no son válidos. Verifica la información e intenta nuevamente.";
        }
      }

      toast.showToast({
        title,
        description,
        options: {
          type: "error",
          duration: 5000,
        },
      });
    },
  });
};

export const useGetNode = () => {
  const toast = useToast();
  return useMutation({
    mutationFn: async (nodeId: string): Promise<ApiNode> => {
      const response = await axios.get(
        `/admin/v1/node/${nodeId}?relationships=${[
          "inputs",
          "inputs.conditions",
          "inputs.calculations",
          "inputs.filterConfiguration.conditions",
          "expiration",
          "expiration.nodeTimeExtension",
          "inputs.sourceConnections",
          "inputs.targetConnections",
        ].join(",")}`,
      );

      const json = response.data?.data;

      return json;
    },
    onError() {
      toast.showToast({
        title: "Error al obtener nodo",
        description: "Ocurrió un error al intentar obtener el nodo.",
        options: {
          type: "error",
          duration: 5000,
        },
      });
    },
  });
};

export const getForm = async (id: string) => {
  const response = await axios.get(`/admin/v1/forms/${id}`);
  return response.data;
};

// get form by id
export const useGetForm = (id: string) => {
  return useQuery({
    queryKey: ["admin-form", id],
    queryFn: async () => {
      const response = await axios.get(`/admin/v1/forms/${id}`);
      return response.data;
    },
    enabled: !!id, // solo ejecuta la query si existe un id
  });
};

export const useUpdateForm = () => {
  const query = useQueryClient();
  const toast = useToast();

  return useMutation({
    mutationFn: async (data: { form: FormattedForm }) => {
      const response = await axios.put(`/admin/v1/forms/${data.form.id}`, {
        ...data.form,
      });
      return response.data;
    },
    onSuccess() {
      query.invalidateQueries({ queryKey: ["admin-forms"] });
      toast.showToast({
        title: "Formulario actualizado",
        description: "El formulario ha sido actualizado correctamente.",
        options: {
          type: "success",
          duration: 5000,
        },
      });
    },
    onError(error: ValidationError) {
      let title = "Error al actualizar el formulario";
      let description =
        "Ocurrió un error al intentar actualizar el formulario.";

      if (error?.response?.status === 422) {
        const validationErrors = error?.response?.data?.errors;

        if (validationErrors?.name?.includes("validation.unique")) {
          title = "Nombre duplicado";
          description =
            "Ya existe un formulario con este nombre. Por favor, elige un nombre diferente.";
        } else {
          title = "Error de validación";
          description =
            "Los datos del formulario no son válidos. Verifica la información e intenta nuevamente.";
        }
      } else if (error?.response?.status === 404) {
        title = "Formulario no encontrado";
        description = "El formulario que intentas actualizar no existe.";
      }

      toast.showToast({
        title,
        description,
        options: {
          type: "error",
          duration: 5000,
        },
      });
    },
  });
};

// Node expiration

export interface NodeExpirationBody {
  /** Nombre visible (<=255) */
  display_name?: string;
  /** Días disponibles (ej. ['monday','tuesday',...]) */
  available_days?: string[];
  /** Unidad del límite: minute | hour | day | month | year */
  limit_unit: "minute" | "hour" | "day" | "week" | "month" | "year";
  /** Cantidad del límite (>= 0) */
  limit_amount: number;
  /** UUID del nodo relacionado (opcional si ya existe asociación en backend) */
  node_id?: string;
}

export interface UpdateNodeExpirationPayload {
  id: string;
  data: Partial<NodeExpirationBody>;
}

export const useGetNodeExpiration = (id: string) => {
  return useQuery({
    queryKey: ["admin-node-expiration", id],
    queryFn: async () => {
      const res = await axios.get(`/admin/v1/node-expiration/${id}`);
      return res.data;
    },
    enabled: !!id,
  });
};

export const useGetNodesById = (id: string | undefined) => {
  return useQuery({
    queryKey: ["admin-node-id", id],
    queryFn: async () => {
      const res = await axios.get(
        `/admin/v1/node/${id}?relationships=${[
          "inputs",
          "inputs.conditions",
          "inputs.calculations",
          "inputs.filterConfiguration.conditions",
          "expiration",
          "expiration.nodeTimeExtension",
          "inputs.sourceConnections",
          "inputs.targetConnections",
        ].join(",")}`,
      );
      return res.data;
    },
    enabled: !!id,
  });
};

export const useCreateNodeExpiration = (nodeId?: string) => {
  const query = useQueryClient();
  const toast = useToast();

  return useMutation({
    mutationFn: async (payload: { expiration: NodeExpirationBody }) => {
      const res = await axios.post("/admin/v1/node-expiration", {
        ...payload.expiration,
      });
      return res.data;
    },
    onSuccess() {
      query.invalidateQueries({ queryKey: ["admin-node-expiration"] });
      if (nodeId) {
        query.invalidateQueries({ queryKey: ["admin-flow-detail", nodeId] });
      }
      toast.showToast({
        title: "Temporizador creado",
        description: "El temporizador del nodo se ha creado correctamente.",
        options: { type: "success", duration: 5000 },
      });
    },
    onError() {
      toast.showToast({
        title: "Error al crear el temporizador",
        description: "Ocurrió un error al intentar crear el temporizador.",
        options: { type: "error", duration: 5000 },
      });
    },
  });
};

export const useUpdateNodeExpiration = (flowId?: string) => {
  const query = useQueryClient();
  const toast = useToast();

  return useMutation({
    mutationFn: async ({ id, data }: UpdateNodeExpirationPayload) => {
      const res = await axios.patch(`/admin/v1/node-expiration/${id}`, {
        ...data,
      });
      return res.data;
    },
    onSuccess() {
      query.invalidateQueries({ queryKey: ["admin-node-expiration"] });
      if (flowId) {
        query.invalidateQueries({ queryKey: ["admin-flow-detail", flowId] });
      }
      toast.showToast({
        title: "Temporizador actualizado",
        description:
          "El temporizador del nodo se ha actualizado correctamente.",
        options: { type: "success", duration: 5000 },
      });
    },
    onError() {
      toast.showToast({
        title: "Error al actualizar el temporizador",
        description: "Ocurrió un error al intentar actualizar el temporizador.",
        options: { type: "error", duration: 5000 },
      });
    },
  });
};

export const useDeleteNodeExpiration = (flowId?: string) => {
  const query = useQueryClient();
  const toast = useToast();

  return useMutation({
    mutationFn: async (id: string) => {
      const res = await axios.delete(`/admin/v1/node-expiration/${id}`);
      return res.data;
    },
    onSuccess() {
      query.invalidateQueries({ queryKey: ["admin-node-expiration"] });
      if (flowId) {
        query.invalidateQueries({ queryKey: ["admin-flow-detail", flowId] });
      }
      toast.showToast({
        title: "Temporizador eliminado",
        description: "El temporizador del nodo se ha eliminado correctamente.",
        options: { type: "success", duration: 5000 },
      });
    },
    onError() {
      toast.showToast({
        title: "Error al eliminar el temporizador",
        description: "Ocurrió un error al intentar eliminar el temporizador.",
        options: { type: "error", duration: 5000 },
      });
    },
  });
};

export const useCreateNodeTimeExtension = () => {
  return useMutation({
    mutationFn: async (data: {
      node_expiration_id: string;
      availableDays: string[];
      leaderRequired: boolean;
      extensionUnit: "minute" | "hour" | "day" | "week" | "month" | "year";
      extensionAmount: number;
    }) => {
      const response = await axios.post("/admin/v1/node-time-extensions", {
        node_expiration_id: data.node_expiration_id,
        available_days: data.availableDays,
        leader_required: data.leaderRequired,
        extension_unit: data.extensionUnit,
        extension_amount: data.extensionAmount,
        display_name: `Prórroga`,
        description: `Prórroga`,
      });
      return response.data;
    },
  });
};

export const useUpdateNodeTimeExtension = () => {
  return useMutation({
    mutationFn: async (data: {
      id: string;
      availableDays: string[];
      leaderRequired: boolean;
      extensionUnit: "minute" | "hour" | "day" | "week" | "month" | "year";
      extensionAmount: number;
    }) => {
      const response = await axios.patch(
        `/admin/v1/node-time-extensions/${data.id}`,
        {
          available_days: data.availableDays,
          leader_required: data.leaderRequired,
          extension_unit: data.extensionUnit,
          extension_amount: data.extensionAmount,
        },
      );
      return response.data;
    },
  });
};

export const useGetNodeTimeExtension = (id: string) => {
  return useQuery({
    queryKey: ["admin-node-time-extension", id],
    queryFn: async () => {
      const response = await axios.get(`/admin/v1/node-time-extensions/${id}`);
      return response.data;
    },
    enabled: !!id,
  });
};

export const useDeleteNodeTimeExtension = () => {
  return useMutation({
    mutationFn: async (id: string) => {
      const response = await axios.delete(
        `/admin/v1/node-time-extensions/${id}`,
      );
      return response.data;
    },
  });
};

export const useDuplicateNode = (flowId: string) => {
  const query = useQueryClient();
  const toast = useToast();
  return useMutation({
    mutationFn: async (data: { node_id: string; flow_id: string }) => {
      const response = await axios.post(
        `/admin/v1/node/duplicate/${data.node_id}/${data.flow_id}`,
      );
      return response.data;
    },
    onSuccess() {
      // Invalidar las queries del flow para actualizar el canvas con el nuevo nodo clonado
      query.invalidateQueries({
        queryKey: ["admin-flow-detail", flowId],
      });
      // También invalidar cualquier query relacionada con flows para asegurar consistencia
      query.invalidateQueries({
        queryKey: ["admin-flow-detail"],
      });
      toast.showToast({
        title: "Nodo clonado",
        description: "El nodo ha sido clonado correctamente.",
        options: {
          type: "success",
          duration: 5000,
        },
      });
    },
    onError() {
      toast.showToast({
        title: "Error al clonar nodo",
        description: "Ocurrió un error al intentar clonar el nodo.",
        options: {
          type: "error",
          duration: 5000,
        },
      });
    },
  });
};

// Clonar nodo formulario
interface CloneNodeBody {
  target_flow_id?: string;
  name?: string;
}

export const useCloneNode = (flowId: string) => {
  const query = useQueryClient();
  const toast = useToast();

  return useMutation({
    mutationFn: async (params: { nodeId: string; body?: CloneNodeBody }) => {
      const { nodeId, body } = params;
      const res = await axios.post(
        `/admin/v1/flows/${flowId}/nodes/${nodeId}/duplicate`,
        body ?? {},
      );
      return res.data;
    },
    onSuccess() {
      query.invalidateQueries({ queryKey: ["admin-flow-detail", flowId] });
      query.invalidateQueries({ queryKey: ["admin-flow-detail"] });

      toast.showToast({
        title: "Nodo clonado",
        description: "El nodo ha sido clonado correctamente.",
        options: { type: "success", duration: 5000 },
      });
    },
    onError(error: ValidationError) {
      let title = "Error al clonar nodo";
      let description =
        error?.response?.data?.message ??
        "Ocurrió un error al intentar clonar el nodo.";

      if (error?.response?.status === 422) {
        const errs = error.response.data?.errors;
        if (errs?.name?.includes("validation.max.string")) {
          title = "Nombre demasiado largo";
          description = "El nombre no debe superar 255 caracteres.";
        } else if (errs?.name?.includes("Ya existe un nodo con este nombre")) {
          title = "Nombre existente";
          description = "Ya existe un nodo con este nombre.";
        } else {
          title = "Ha ocurrido un error";
          description =
            "Los datos proporcionados no son válidos. Verifica la información e intenta nuevamente.";
        }
      } else if (error?.response?.status === 404) {
        title = "No encontrado";
        description = "No se encontró el flujo o el nodo que intentas clonar.";
      } else if (error?.response?.status === 403) {
        title = "Sin permisos";
        description = "No tienes permisos para clonar nodos en este flujo.";
      }

      toast.showToast({
        title,
        description,
        options: { type: "error", duration: 5000 },
      });
    },
  });
};

export const createDataCenterFetcher = (
  queryClient: QueryClient,
): ExternalDataFunction => {
  return async (
    table_name: string,
    source: string | undefined | null = "SDT",
    filters:
      | Array<{
          name: string;
          operator?: string;
          type: string;
          value: string | number | boolean | null;
        }>
      | undefined
      | null = [],
    queryFilters: {
      search?: string;
      page?: number;
    } = {},
    column: string | null = null,
  ) => {
    try {
      const data = await queryClient.fetchQuery({
        queryKey: [
          "data-center-table-data",
          table_name,
          source,
          filters,
          queryFilters,
          column,
        ],
        queryFn: async (): Promise<ExternalData> => {
          const response = await axios.get(
            "/admin/v1/data-center/tables/data",
            {
              params: {
                table_name,
                source,
                paginate: 1,
                filters: {
                  ...filters,
                },
                filter: {
                  ...(queryFilters.search
                    ? {
                        search: queryFilters.search,
                      }
                    : {}),
                },
                order_by: column,
                page: queryFilters.page || 1,
              },
            },
          );
          return {
            data: response.data.data.data,
            current_page: response.data.data.current_page,
            last_page: response.data.data.last_page,
          };
        },
        staleTime: 5 * 60 * 1000,
      });
      return data;
    } catch (error) {
      console.error(`Error fetching data from table ${table_name}:`, error);
      return { data: [] };
    }
  };
};

export const createInteroperabilityFetcher = (queryClient: QueryClient) => {
  // console.log("createInteroperabilityFetcher - queryClient:", queryClient);
  // console.log("createInteroperabilityFetcher - queryClient data:", {
  //   queries: queryClient.getQueryCache().getAll().length,
  //   mutations: queryClient.getMutationCache().getAll().length,
  // });

  // eslint-disable-next-line react-hooks/rules-of-hooks
  const toast = useToast();

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return async (config: any, inputData: any) => {
    try {
      const { service, action, params } = config || {};

      if (!service || !action) {
        console.warn("Service or action missing in interoperability config");
        return {};
      }

      const data = await queryClient.fetchQuery({
        queryKey: [
          "interoperability-query",
          service,
          action,
          params,
          inputData,
        ],
        retry: 0,
        queryFn: async () => {
          const paramsObject: Record<
            string,
            string | number | boolean | null | undefined
          > = {};

          // case-numbers: registry_number, company_registry_number
          // case-strings: legal_rep_dui, owner_dui

          /* caso descpnocido: 
           -cadastral_key (escritura inmueble), 
           -company_nit (Matricula de empresa) 0614-151290-102-3 
          */

          if (config.input_field && Array.isArray(config.input_field)) {
            if (config.input_field.length === 1) {
              const fieldName = config.input_field[0].name;
              if (
                typeof inputData === "object" &&
                inputData !== null &&
                fieldName in inputData
              ) {
                paramsObject[fieldName] = [
                  "registry_number",
                  "company_registry_number",
                ].includes(fieldName)
                  ? Number(inputData[fieldName])
                  : ["legal_rep_dui", "owner_dui"].includes(fieldName)
                    ? (inputData[fieldName]?.toString() ?? inputData[fieldName])
                    : inputData[fieldName];
              } else {
                paramsObject[fieldName] = inputData;
              }
            } else {
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              config.input_field.forEach((field: any) => {
                if (typeof inputData === "object" && inputData !== null) {
                  paramsObject[field.name] = inputData[field.name];
                } else {
                  paramsObject[field.name] = inputData;
                }
              });
            }
          }

          const payload: {
            service: string;
            action: string;
            params?: Record<
              string,
              string | number | boolean | null | undefined
            >;
          } = {
            service,
            action,
          };

          // Solo agregar params si tiene propiedades
          if (Object.keys(paramsObject).length > 0) {
            payload.params = paramsObject;
          }

          const response = await axios.post(
            "/admin/v1/interoperability/query",
            payload,
          );

          // Response format:
          // {
          //   "success": true,
          //   "message": "personal_information",
          //   "data": { ... }
          // }

          return response.data || null;
        },
        staleTime: 0,
      });

      return {
        data,
        config,
      };
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: ValidationError | any) {
      console.error(`Error fetching interoperability data:`, error);
      let description =
        "Ocurrió un error al intentar obtener los datos de interoperabilidad.";

      if (error.response.status === 400) {
        const errs = error.response.data;
        console.log("errs:", errs);
        if (
          errs?.message?.includes(
            "Los parámetros proporcionados no son válidos para la acción especificada.",
          )
        ) {
          description =
            "Los datos proporcionados no son válidos. Verifica la información e intenta nuevamente.";
        }
      } else if (error.response.status === 404) {
        description = "No existen datos para la consulta realizada.";
      }

      toast.showToast({
        title: "Error de interoperabilidad",
        description,
        options: { type: "error", duration: 5000 },
      });

      return {};
    }
  };
};

export const createDataByIdFetcher = (queryClient: QueryClient) => {
  return async (table: string, source: string, id: string) => {
    try {
      const data = await queryClient.fetchQuery({
        queryKey: ["data-center", "table-data-by-id", table, id],
        queryFn: async (): Promise<
          | {
              id: string | number;
              [key: string]: ExternalDataType;
            }
          | undefined
        > => {
          const response = await axios.get(
            "/admin/v1/data-center/tables/data",
            {
              params: {
                table_name: table,
                source,
                "filters[0][name]": "id",
                "filters[0][operator]": "EQUALS",
                "filters[0][value]": id,
                "filters[0][type]": "STRING",
              },
            },
          );
          return response.data;
        },
        staleTime: 5 * 60 * 1000, // Cache for 5 minutes
      });

      return data;
    } catch (error) {
      console.error(
        `Error fetching data from table ${table} by id ${id}:`,
        error,
      );
      return undefined;
    }
  };
};

export const useRenameConnectionLabel = (flowId: string) => {
  const query = useQueryClient();
  return useMutation({
    mutationFn: async ({
      connectionId,
      label,
    }: {
      connectionId: string;
      label: string;
    }) => {
      const response = await axios.patch(
        `/admin/v1/node/connection/${connectionId}/label`,
        {
          display_name: label,
        },
      );
      return response.data;
    },
    onSuccess() {
      query.invalidateQueries({ queryKey: ["admin-flow-detail", flowId] });
      query.invalidateQueries({ queryKey: ["admin-flow-detail"] });
    },
  });
};
